import TimeSlotSelect from '../v2/TimeSlotSelect';
import DateSelect, { DateSelectProps } from '../DateSelect';
import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import formatNonZeroAvailabilityForDaysOfWeek from '../DateSelect/functions';
import { serviceProviderAvailavilityHandleRecommendedStartDateTime } from './functions';
import NoMoreTimeSlotsAlert from '../NoMoreTimeSlotsAlert';

type ServiceProviderAvailabilityTimeSlotsProps = {
  initialValue?: number;
  onSelectDate?: (v: number) => void;
  onSelectTime?: (v: number) => void;
  getTimeSlots: (from: number, to: number) => Promise<number[] | null> | number[];
  dateSlots: number[];
  onSelectMonth: (v: number) => void;
  hideDateSelect?: boolean;
  recommendedStartDateTime?: number;
  dateSelectProps?: Partial<DateSelectProps>;
  disabled?: boolean;
  children?: React.ReactNode;
}

const now = dayjs();

const ServiceProviderAvailability: React.FC<
  ServiceProviderAvailabilityTimeSlotsProps
> = ({
  initialValue,
  onSelectDate,
  onSelectTime,
  getTimeSlots,
  dateSlots: availableDates,
  onSelectMonth,
  hideDateSelect = false,
  recommendedStartDateTime,
  dateSelectProps,
  disabled = false,
  children,
}) => {
  const [dateUnix, setDateUnix] = useState<number | undefined>(initialValue || recommendedStartDateTime);
  const [monthUnix, setMonthUnix] = useState(
    dateUnix ? dayjs.unix(dateUnix).startOf('M').unix() : undefined
  );
  const [timeUnix, setTimeUnix] = useState<number | undefined>(initialValue);
  const [timeSlots, setTimeSlots] = useState<number[] | null>(null);

  const availabilityMessage = useMemo(
    () => availableDates.length ?
      `This provider is available ${formatNonZeroAvailabilityForDaysOfWeek(availableDates)}.` :
      '',
    [availableDates],
  );

  const getDateSelect = () => {
    if (hideDateSelect) {
      return <></>;
    }

    return (
      <DateSelect
        {...dateSelectProps}
        value={dateUnix}
        onMonthChange={m => {
          const mUnix = m.unix();
          setMonthUnix(mUnix);
          onSelectMonth(mUnix);
        }}
        monthUnix={monthUnix}
        onSelect={v => {
          setDateUnix(v);
        }}
        slots={availableDates}
        availability={availabilityMessage}
        disabled={disabled}
      />
    );
  };

  useEffect(() => {
    (async () => {
      if (disabled) {
        return [];
      }

      if (dateUnix) {
        const dateSlotUnix = dayjs.unix(dateUnix);
        const startOfDate = dateSlotUnix.clone().startOf('d').unix();
        const endOfDate = dateSlotUnix.clone().endOf('d').unix();

        const slots = await getTimeSlots(startOfDate, endOfDate);
        setTimeSlots(slots);
      }

      return [];
    })();
  }, [dateUnix, disabled]);

  useEffect(() => {
    if (disabled || !timeSlots) {
      return;
    }

    serviceProviderAvailavilityHandleRecommendedStartDateTime(
      timeUnix,
      recommendedStartDateTime,
      timeSlots,
      v => {
        setTimeUnix(v);
        setDateUnix(v);
      },
    );
  }, [timeSlots, recommendedStartDateTime, disabled]);

  const getTimeSlotSelect = () => {
    return (
      <div>
        <span className="text-2xl font-bold lg:hidden">
            Choose a time
        </span>
        {timeSlots ? (
          <TimeSlotSelect
            value={dateUnix}
            onSelect={v => {
              setTimeUnix(v);
              const t = dayjs.unix(v);
              if (dateUnix) {
                let d = dayjs.unix(dateUnix);
                d = d.hour(t.hour());
                d = d.minute(t.minute());
                setDateUnix(d.unix());
              }
            }}
            slots={timeSlots}
            empty={disabled ? (
              <>
                <div className="skeleton-lg" />
                <div className="skeleton-lg" />
                <div className="skeleton-md" />
              </>
            ) : (
              <NoMoreTimeSlotsAlert
                dateUnix={dateUnix}
                copy="No more available times"
              />
            )}
            disabled={disabled}
          />
        ) : (
          <>
            <div className="skeleton-lg" />
            <div className="skeleton-lg" />
            <div className="skeleton-lg" />
            <div className="skeleton-md" />
          </>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (dateUnix && timeUnix) {
      const dateDayJs = dayjs.unix(dateUnix);
      const timeDayJs = dayjs.unix(timeUnix);
      const dateTimeDayJs = dateDayJs
        .hour(timeDayJs.hour())
        .minute(timeDayJs.minute())
        .second(0).millisecond(0);

      onSelectTime && onSelectTime(dateTimeDayJs.unix());
    }
  }, [timeUnix, dateUnix]);

  useEffect(() => {
    if (dateUnix) {
      onSelectDate && onSelectDate(dateUnix);
    }
  }, [dateUnix]);

  useEffect(() => {
    if (disabled) {
      return;
    }

    if (!dateUnix && availableDates.length) {
      const nextAvailableDate = availableDates[0];
      const tomorrow = now.clone().add(1, 'd');

      if (dayjs.unix(nextAvailableDate).isAfter(tomorrow)) {
        setDateUnix(nextAvailableDate);
      } else {
        const nextNextAvailableDate = availableDates[1];
        if (dayjs.unix(nextNextAvailableDate).isAfter(tomorrow)) {
          setDateUnix(nextNextAvailableDate);
        }
      }
    }
  }, [availableDates, disabled]);

  return (
    <>
      {getDateSelect()}
      <div className="">
        {Boolean(dateUnix) && getTimeSlotSelect()}
        {children}
      </div>
    </>
  );
};

export default ServiceProviderAvailability;
